import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

export default function FinancingOptions() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => {
      if (sectionRef.current) observer.unobserve(sectionRef.current);
    };
  }, []);

  const options = [
    {
      icon: 'ri-bank-card-line',
      title: 'Flexible Terms',
      description: 'Choose from 24 to 84-month financing plans tailored to your budget and lifestyle.',
    },
    {
      icon: 'ri-percent-line',
      title: 'Competitive Rates',
      description: 'Access exclusive rates through our network of trusted lenders and financial partners.',
    },
    {
      icon: 'ri-file-check-line',
      title: 'Fast Approval',
      description: 'Get pre-approved in minutes with our streamlined online application process.',
    },
  ];

  return (
    <section id="financing" ref={sectionRef} className="py-20 sm:py-28 px-4 sm:px-8 lg:px-16 bg-[#0d0d0d]">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Content */}
          <div className={`transition-all duration-1000 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
            <div className="flex items-center gap-4 mb-5">
              <div className="h-px w-10 bg-[#C9A84C]"></div>
              <span className="text-[#C9A84C] text-xs tracking-[0.4em] uppercase" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                Financing Solutions
              </span>
            </div>
            <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6 leading-tight" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              Drive Your Dream.
              <span className="block text-[#C9A84C]">Finance with Ease.</span>
            </h2>
            <p className="text-white/70 mb-8 leading-relaxed text-sm" style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 300 }}>
              At Paramount Motors, we believe financing should be as smooth as the ride. Whether you're looking for low monthly payments or a quick payoff, our team works with top-tier lenders to secure the best rates for your situation.
            </p>
            <div className="space-y-5 mb-8">
              {options.map((option, index) => (
                <div
                  key={option.title}
                  className={`flex items-start gap-4 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}
                  style={{ transitionDelay: `${300 + index * 150}ms` }}
                >
                  <div className="w-12 h-12 flex items-center justify-center border border-[#C9A84C]/30 rounded-sm flex-shrink-0">
                    <i className={`${option.icon} text-[#C9A84C] text-xl`}></i>
                  </div>
                  <div>
                    <h3 className="text-white text-base font-semibold mb-1" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                      {option.title}
                    </h3>
                    <p className="text-white/70 text-sm leading-relaxed" style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 300 }}>
                      {option.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
            <Link
              to="/financing"
              className="inline-flex items-center gap-3 bg-[#C9A84C] hover:bg-[#e0bc5e] text-[#0d0d0d] px-8 py-4 rounded-sm text-xs font-bold tracking-widest uppercase transition-all cursor-pointer whitespace-nowrap hover:scale-105 transform"
              style={{ fontFamily: 'Montserrat, sans-serif' }}
            >
              Apply for Financing
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>

          {/* Image */}
          <div className={`transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}>
            <div className="relative">
              <div className="relative w-full h-80 lg:h-[520px] rounded-sm overflow-hidden">
                <img
                  src="https://public.readdy.ai/ai/img_res/e6eff3630f761a0eb9fb92b4290261c7.jpg"
                  alt="Financing Options"
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
              </div>
              <div className="hidden sm:block absolute -bottom-4 -left-4 w-full h-full border border-[#C9A84C]/30 rounded-sm pointer-events-none"></div>
              <div className="absolute bottom-6 left-6 right-6 bg-[#0d0d0d]/90 backdrop-blur-sm p-5 rounded-sm border border-[#C9A84C]/20">
                <p className="text-[#C9A84C] text-xs tracking-widest uppercase mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  Quick Stats
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-2xl font-bold text-white" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                      4.9%
                    </p>
                    <p className="text-white/70 text-xs" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                      Starting APR
                    </p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-white" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                      24hrs
                    </p>
                    <p className="text-white/70 text-xs" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                      Avg. Approval
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}